import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { motion } from 'framer-motion';
import { useSEO } from '../../utils/seo';

interface FormData {
  // Step 1 - Afmetingen
  breedte: number;
  hoogte: number;
  
  // Step 2 - TV & audio
  tvFormaat: string;
  tvFormaatCustom: string;
  soundbar: string;
  
  // Step 3 - Haard
  haard: string;
  
  // Step 4 - Contact
  naam: string;
  email: string;
  telefoon: string;
  fotoUpload: File[];
  extraWensen: string;
}

export default function CinewallConfigurator() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    breedte: 320,
    hoogte: 260,
    tvFormaat: '',
    tvFormaatCustom: '',
    soundbar: '',
    haard: '',
    naam: '',
    email: '',
    telefoon: '',
    fotoUpload: [],
    extraWensen: '',
  });
  
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 0 });
  const [uploadError, setUploadError] = useState('');

  const totalSteps = 4;

  useSEO({
    title: 'Cinewall configurator | Stel uw cinewall samen en ontvang indicatie',
    description: 'Stel uw cinewall samen. Kies afmetingen, haard, kleurpalet en opties en ontvang direct een prijsindicatie. Offerte en advies binnen 24 uur.',
    keywords: 'cinewall configurator, cinewall samenstellen, cinewall prijs berekenen, cinewall op maat',
    canonical: `${import.meta.env.VITE_SITE_URL}/cinewall-configurator`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'Cinewall Configurator',
      description: 'Interactieve tool om uw cinewall samen te stellen en direct een prijsindicatie te ontvangen',
      url: `${import.meta.env.VITE_SITE_URL}/cinewall-configurator`,
      applicationCategory: 'DesignApplication',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'EUR',
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);

    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    calculatePrice();
  }, [formData]);

  const calculatePrice = () => {
    let minPrice = 2500;
    let maxPrice = 3500; // Basis range: €2.500 - €3.500

    // Extra breedte/hoogte
    const totalSize = formData.breedte + formData.hoogte;
    if (totalSize > 600) {
      minPrice += 300;
      maxPrice += 800;
    }
    if (totalSize > 700) {
      minPrice += 500;
      maxPrice += 1500;
    }
    if (totalSize > 800) {
      minPrice += 800;
      maxPrice += 2000;
    }

    // Haard
    if (formData.haard === 'elektrisch') {
      minPrice += 1200;
      maxPrice += 2500;
    } else if (formData.haard === 'waterdamp') {
      minPrice += 2000;
      maxPrice += 4000;
    }

    setPriceRange({ min: minPrice, max: maxPrice });
  };

  const updateFormData = (field: string, value: string | number | File[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    setUploadError('');
    const validFiles: File[] = [];
    const maxSize = 10 * 1024 * 1024; // 10 MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic'];

    // Check existing files count
    const currentCount = formData.fotoUpload.length;
    const newFilesArray = Array.from(files);

    if (currentCount + newFilesArray.length > 3) {
      setUploadError('U kunt maximaal 3 foto\'s uploaden');
      return;
    }

    for (const file of newFilesArray) {
      // Check file type
      if (!allowedTypes.includes(file.type.toLowerCase())) {
        setUploadError(`${file.name}: alleen JPG, JPEG, PNG en HEIC zijn toegestaan`);
        continue;
      }

      // Check file size
      if (file.size > maxSize) {
        setUploadError(`${file.name}: bestand is te groot (max 10 MB)`);
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      updateFormData('fotoUpload', [...formData.fotoUpload, ...validFiles]);
    }

    // Reset input
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    const newFiles = formData.fotoUpload.filter((_, i) => i !== index);
    updateFormData('fotoUpload', newFiles);
    setUploadError('');
  };

  const validateStep = (step: number): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (step === 4) {
      if (!formData.naam.trim()) {
        newErrors.naam = 'Naam is verplicht';
      }
      if (!formData.email.trim()) {
        newErrors.email = 'E-mailadres is verplicht';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Ongeldig e-mailadres';
      }
      if (!formData.telefoon.trim()) {
        newErrors.telefoon = 'Telefoonnummer is verplicht';
      } else if (!/^\+?[\d\s-]{8,}$/.test(formData.telefoon)) {
        newErrors.telefoon = 'Ongeldig telefoonnummer (minimaal 8 cijfers)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(prev => prev + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateStep(4)) {
      return;
    }

    setIsSubmitting(true);

    try {
      const now = new Date();
      const datumIngediend = now.toLocaleDateString('nl-NL');
      const tijdIngediend = now.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
      
      // Convert files to base64 for webhook
      const filesBase64 = await Promise.all(
        formData.fotoUpload.map(async (file) => {
          return new Promise<{ name: string; type: string; size: number; data: string }>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve({
                name: file.name,
                type: file.type,
                size: file.size,
                data: reader.result as string
              });
            };
            reader.readAsDataURL(file);
          });
        })
      );

      // Prepare webhook payload
      const webhookPayload = {
        form_type: 'configurator',
        form_name: 'Cinewall Configurator',
        page_url: '/cinewall-configurator',
        datum_ingediend: datumIngediend,
        tijd_ingediend: tijdIngediend,
        timestamp: now.toISOString(),
        
        // Contact info
        naam: formData.naam,
        email: formData.email,
        telefoon: formData.telefoon,
        
        // Configuration
        breedte_cinewall: `${formData.breedte} cm`,
        hoogte_cinewall: `${formData.hoogte} cm`,
        tv_formaat: formData.tvFormaat === 'anders' ? formData.tvFormaatCustom : formData.tvFormaat || '-',
        soundbar: formData.soundbar || '-',
        haard_type: formData.haard || '-',
        
        // Price indication
        indicatie_prijs: `€${priceRange.min.toLocaleString('nl-NL')} - €${priceRange.max.toLocaleString('nl-NL')}`,
        prijs_min: priceRange.min,
        prijs_max: priceRange.max,
        
        // Additional info
        opmerkingen: formData.extraWensen || '-',
        
        // Files
        fotos: filesBase64,
        aantal_fotos: filesBase64.length,
        
        // Source
        bron: 'Configurator'
      };

      console.log('🚀 Sending to Make.com webhook...', {
        url: 'https://hook.eu1.make.com/ph2tsrq88dy9uurcqxt423mg5tx39lbf',
        payload: webhookPayload
      });

      // Send to Make.com webhook - WAIT FOR COMPLETION
      const webhookResponse = await fetch('https://hook.eu1.make.com/ph2tsrq88dy9uurcqxt423mg5tx39lbf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookPayload),
      });

      console.log('✅ Webhook response:', {
        status: webhookResponse.status,
        statusText: webhookResponse.statusText,
        ok: webhookResponse.ok
      });

      if (!webhookResponse.ok) {
        const errorText = await webhookResponse.text();
        console.error('❌ Webhook failed:', errorText);
        throw new Error(`Webhook failed: ${webhookResponse.status}`);
      }

      console.log('✅ Webhook succeeded!');

      // Success!
      setSubmitSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (error) {
      console.error('❌ Submit error:', error);
      alert('Er is iets misgegaan bij het versturen. Probeer het opnieuw of neem contact met ons op via WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-20 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
              <i className="ri-check-line text-4xl text-green-600"></i>
            </div>
            <h1 className="text-4xl md:text-5xl font-serif text-gray-900 mb-6">
              Bedankt! Uw aanvraag is verzonden.
            </h1>
            <p className="text-xl text-gray-600 mb-4 leading-relaxed">
              Wij nemen binnen 24 uur contact met u op.
            </p>
            
            <div className="bg-stone-50 rounded-lg p-8 mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Uw prijsindicatie</h3>
              <div className="text-3xl font-serif text-[#C7A86A] mb-2">
                €{priceRange.min.toLocaleString('nl-NL')} - €{priceRange.max.toLocaleString('nl-NL')}
              </div>
              <p className="text-sm text-gray-600">
                Dit is een indicatie op basis van uw keuzes. Wij geven u persoonlijk advies en een offerte op maat.
              </p>
            </div>

            {/* Calendly Section */}
            <div className="bg-[#C7A86A]/10 border border-[#C7A86A]/30 rounded-lg p-8 mb-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <i className="ri-calendar-line text-3xl text-[#C7A86A]"></i>
                <h3 className="text-2xl font-serif text-gray-900">
                  Wilt u direct een showroomafspraak plannen?
                </h3>
              </div>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Plan direct uw showroomafspraak en ervaar materialen, haarden en verlichting in het echt.
              </p>
              <Link
                to="/cinewall-showroom"
                className="inline-block bg-[#C7A86A] text-[#0F0F0F] px-8 py-4 rounded-md font-semibold text-base hover:bg-[#B89858] transition-all duration-300 whitespace-nowrap"
              >
                Plan showroomafspraak
              </Link>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="border border-[#C7A86A] text-[#C7A86A] px-8 py-4 rounded-md font-semibold text-base hover:bg-[#C7A86A]/10 transition-all duration-300 whitespace-nowrap"
              >
                Terug naar home
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const renderProgressBar = () => (
    <div className="mb-12">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-semibold text-gray-600">
          Stap {currentStep} van {totalSteps}
        </span>
        <span className="text-sm text-gray-500">
          {Math.round((currentStep / totalSteps) * 100)}% voltooid
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-[#C7A86A] h-2 rounded-full transition-all duration-300"
          style={{ width: `${(currentStep / totalSteps) * 100}%` }}
        ></div>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-serif text-gray-900 mb-3">Afmetingen</h2>
        <p className="text-gray-600">Wat zijn de afmetingen van uw cinewall?</p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-4">
          Breedte: <span className="text-[#C7A86A] text-2xl font-serif">{formData.breedte} cm</span>
        </label>
        <input
          type="range"
          min="100"
          max="700"
          step="1"
          value={formData.breedte}
          onChange={(e) => updateFormData('breedte', parseInt(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#C7A86A]"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-2">
          <span>100 cm</span>
          <span>700 cm</span>
        </div>
        <p className="text-sm text-gray-600 mt-3 italic">
          Tip: meet de beschikbare wandbreedte en kies een maat die past bij uw ruimte.
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-4">
          Hoogte: <span className="text-[#C7A86A] text-2xl font-serif">{formData.hoogte} cm</span>
        </label>
        <input
          type="range"
          min="200"
          max="400"
          step="1"
          value={formData.hoogte}
          onChange={(e) => updateFormData('hoogte', parseInt(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#C7A86A]"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-2">
          <span>200 cm</span>
          <span>400 cm</span>
        </div>
        <p className="text-sm text-gray-600 mt-3 italic">
          Kies bijvoorbeeld halfhoog of tot plafond, afhankelijk van uw interieur.
        </p>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-serif text-gray-900 mb-3">TV & audio</h2>
        <p className="text-gray-600">Welke apparatuur wilt u integreren?</p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          TV formaat
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
          {['55', '65', '75', '85', 'anders'].map(option => (
            <button
              key={option}
              type="button"
              onClick={() => updateFormData('tvFormaat', option)}
              className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                formData.tvFormaat === option
                  ? 'border-[#C7A86A] bg-[#C7A86A]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {option === 'anders' ? 'Anders' : `${option} inch`}
            </button>
          ))}
        </div>
        {formData.tvFormaat === 'anders' && (
          <input
            type="text"
            value={formData.tvFormaatCustom}
            onChange={(e) => updateFormData('tvFormaatCustom', e.target.value)}
            placeholder="Vul TV formaat in (inch)"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C7A86A] focus:border-transparent text-sm"
          />
        )}
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Soundbar
        </label>
        <div className="grid grid-cols-2 gap-3">
          {['ja', 'nee'].map(option => (
            <button
              key={option}
              type="button"
              onClick={() => updateFormData('soundbar', option)}
              className={`p-4 rounded-lg border-2 transition-all capitalize cursor-pointer ${
                formData.soundbar === option
                  ? 'border-[#C7A86A] bg-[#C7A86A]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-serif text-gray-900 mb-3">Haard</h2>
        <p className="text-gray-600">Wilt u een haard in uw cinewall?</p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Haard type
        </label>
        <div className="grid grid-cols-3 gap-3">
          {['geen', 'elektrisch', 'waterdamp'].map(option => (
            <button
              key={option}
              type="button"
              onClick={() => updateFormData('haard', option)}
              className={`p-4 rounded-lg border-2 transition-all capitalize cursor-pointer ${
                formData.haard === option
                  ? 'border-[#C7A86A] bg-[#C7A86A]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      {/* Show price indication after step 3 */}
      <div className="bg-stone-50 rounded-lg p-8 mt-8">
        <h3 className="text-2xl font-serif text-gray-900 mb-4">Uw prijsindicatie</h3>
        <div className="text-4xl font-serif text-[#C7A86A] mb-4">
          €{priceRange.min.toLocaleString('nl-NL')} - €{priceRange.max.toLocaleString('nl-NL')}
        </div>
        <p className="text-gray-600 leading-relaxed mb-4">
          Dit is een indicatie op basis van uw keuzes. Wij nemen binnen 24 uur contact met u op.
        </p>
        <div className="bg-white rounded-lg p-4 border border-stone-200">
          <p className="text-sm text-gray-700 flex items-start">
            <i className="ri-information-line text-[#C7A86A] mr-2 mt-0.5 flex-shrink-0"></i>
            <span>Maatwerk; exacte prijs volgt na advies en inmeting</span>
          </p>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-serif text-gray-900 mb-3">Contactgegevens</h2>
        <p className="text-gray-600">Hoe kunnen wij u bereiken?</p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Naam <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="naam"
          value={formData.naam}
          onChange={(e) => updateFormData('naam', e.target.value)}
          placeholder="Uw naam"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C7A86A] focus:border-transparent text-sm"
        />
        {errors.naam && <p className="text-red-500 text-sm mt-2">{errors.naam}</p>}
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          E-mailadres <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={(e) => updateFormData('email', e.target.value)}
          placeholder="uw@email.nl"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C7A86A] focus:border-transparent text-sm"
        />
        {errors.email && <p className="text-red-500 text-sm mt-2">{errors.email}</p>}
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Telefoonnummer <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          name="telefoon"
          value={formData.telefoon}
          onChange={(e) => updateFormData('telefoon', e.target.value)}
          placeholder="06 12345678"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C7A86A] focus:border-transparent text-sm"
        />
        {errors.telefoon && <p className="text-red-500 text-sm mt-2">{errors.telefoon}</p>}
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Upload foto van de muur <span className="text-gray-400 font-normal">(optioneel)</span>
        </label>
        <p className="text-sm text-gray-600 mb-3">
          Een foto helpt ons om sneller en gerichter te adviseren.
        </p>
        <p className="text-xs text-gray-500 mb-3 italic">
          Tip: maak de foto recht van voren en neem de volledige wand mee in beeld.
        </p>
        
        {/* File Upload Input */}
        <div className="relative">
          <input
            type="file"
            id="fotoUpload"
            accept="image/jpeg,image/jpg,image/png,image/heic"
            multiple
            onChange={handleFileUpload}
            className="hidden"
            disabled={formData.fotoUpload.length >= 3}
          />
          <label
            htmlFor="fotoUpload"
            className={`flex items-center justify-center w-full px-4 py-6 border-2 border-dashed rounded-lg transition-all cursor-pointer ${
              formData.fotoUpload.length >= 3
                ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                : 'border-gray-300 hover:border-[#C7A86A] bg-white'
            }`}
          >
            <div className="text-center">
              <i className="ri-upload-cloud-2-line text-3xl text-gray-400 mb-2"></i>
              <p className="text-sm text-gray-600">
                {formData.fotoUpload.length >= 3
                  ? 'Maximaal 3 foto\'s bereikt'
                  : 'Klik om foto\'s te uploaden'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                JPG, JPEG, PNG, HEIC (max 10 MB per foto, max 3 foto's)
              </p>
            </div>
          </label>
        </div>

        {/* Upload Error */}
        {uploadError && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600 flex items-start">
              <i className="ri-error-warning-line mr-2 mt-0.5 flex-shrink-0"></i>
              {uploadError}
            </p>
          </div>
        )}

        {/* File Preview */}
        {formData.fotoUpload.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-sm font-semibold text-gray-700">
              Geüploade foto's ({formData.fotoUpload.length}/3):
            </p>
            <div className="grid grid-cols-1 gap-3">
              {formData.fotoUpload.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-200"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg border border-stone-200 flex-shrink-0">
                      <i className="ri-image-line text-xl text-[#C7A86A]"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="ml-3 w-8 h-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-lg transition-all flex-shrink-0"
                    aria-label="Verwijder foto"
                  >
                    <i className="ri-delete-bin-line text-lg"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Extra wensen <span className="text-gray-400 font-normal">(optioneel, max 500 tekens)</span>
        </label>
        <textarea
          value={formData.extraWensen}
          onChange={(e) => {
            if (e.target.value.length <= 500) {
              updateFormData('extraWensen', e.target.value);
            }
          }}
          placeholder="Heeft u nog specifieke wensen of opmerkingen?"
          rows={4}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C7A86A] focus:border-transparent text-sm resize-none"
        />
        <p className="text-sm text-gray-500 mt-1">{formData.extraWensen.length}/500 tekens</p>
      </div>

      {Object.keys(errors).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 text-sm">
            Vul uw e-mailadres en telefoonnummer in om uw configuratie te verzenden.
          </p>
        </div>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">
              Cinewall configurator – stel uw cinewall samen
            </h1>
            <p className="text-lg text-gray-600">
              Beantwoord enkele vragen en ontvang direct een prijsindicatie
            </p>
          </div>

          {/* WhatsApp Help Button - Direct Link */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed bottom-8 right-8 z-50"
          >
            <a
              href="https://wa.me/31624172293?text=Hallo,%20ik%20ben%20bezig%20met%20de%20cinewall%20configurator%20en%20heb%20een%20vraag..."
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-br from-[#E8F5E9] to-[#C8E6C9] text-gray-900 px-5 py-3 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-3 border border-[#81C784]/30 group"
              aria-label="Hulp nodig?"
            >
              <div className="w-11 h-11 bg-[#25D366] rounded-full flex items-center justify-center shadow-md">
                <i className="ri-whatsapp-line text-2xl text-white"></i>
              </div>
              <div className="text-left pr-2">
                <div className="text-sm font-semibold text-gray-800">Hulp nodig?</div>
                <div className="text-xs text-gray-600">Chat met ons via WhatsApp</div>
              </div>
            </a>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit}>
                {renderProgressBar()}
                {renderCurrentStep()}

                {/* Navigation Buttons */}
                <div className="flex gap-4 mt-12">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={prevStep}
                      className="flex-1 border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-arrow-left-line mr-2"></i>
                      Vorige
                    </button>
                  )}
                  {currentStep < totalSteps ? (
                    <button
                      type="button"
                      onClick={nextStep}
                      className="flex-1 bg-[#C7A86A] text-[#0F0F0F] px-8 py-4 rounded-lg font-semibold hover:bg-[#B89858] transition-all whitespace-nowrap cursor-pointer"
                    >
                      Volgende
                      <i className="ri-arrow-right-line ml-2"></i>
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-[#C7A86A] text-[#0F0F0F] px-8 py-4 rounded-lg font-semibold hover:bg-[#B89858] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {isSubmitting ? 'Verzenden...' : 'Verstuur configuratie'}
                      <i className="ri-send-plane-line ml-2"></i>
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-32 bg-stone-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Uw configuratie</h3>
                
                {priceRange.min > 0 && (
                  <div className="mb-6 pb-6 border-b border-stone-200">
                    <p className="text-sm text-gray-600 mb-2">Indicatieve prijs</p>
                    <div className="text-2xl font-serif text-[#C7A86A]">
                      €{priceRange.min.toLocaleString('nl-NL')} - €{priceRange.max.toLocaleString('nl-NL')}
                    </div>
                  </div>
                )}

                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Breedte:</span>
                    <span className="font-semibold text-gray-900">{formData.breedte} cm</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Hoogte:</span>
                    <span className="font-semibold text-gray-900">{formData.hoogte} cm</span>
                  </div>
                  {formData.tvFormaat && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">TV:</span>
                      <span className="font-semibold text-gray-900">
                        {formData.tvFormaat === 'anders' ? formData.tvFormaatCustom : formData.tvFormaat} inch
                      </span>
                    </div>
                  )}
                  {formData.haard && formData.haard !== 'geen' && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Haard:</span>
                      <span className="font-semibold text-gray-900 capitalize">{formData.haard}</span>
                    </div>
                  )}
                </div>

                <div className="mt-6 pt-6 border-t border-stone-200">
                  <div className="space-y-3">
                    <div className="flex items-start text-sm">
                      <i className="ri-check-line text-[#C7A86A] mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-600">Persoonlijk advies</span>
                    </div>
                    <div className="flex items-start text-sm">
                      <i className="ri-check-line text-[#C7A86A] mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-600">Reactie binnen 24 uur</span>
                    </div>
                    <div className="flex items-start text-sm">
                      <i className="ri-check-line text-[#C7A86A] mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-600">Vrijblijvend</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-20 max-w-4xl mx-auto">
            <h2 className="text-3xl font-serif text-gray-900 mb-8 text-center">
              Veelgestelde vragen over de cinewall configurator
            </h2>
            <div className="space-y-6">
              <div className="bg-stone-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Wat kost een cinewall op maat?
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  De prijs van een cinewall start vanaf circa €2.500 en loopt op 
                  afhankelijk van afmetingen, afwerking, opbergruimte en type haard. 
                  Via onze configurator ontvangt u snel een eerste indicatie.
                </p>
              </div>

              <div className="bg-stone-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Hoe lang duurt het maken van een cinewall?
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Dit verschilt per ontwerp. 
                  Na akkoord op ontwerp en planning wordt uw cinewall 
                  op maat geproduceerd en professioneel gemonteerd.
                </p>
              </div>

              <div className="bg-stone-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Kan elke tv in een cinewall?
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Ja, vrijwel alle tv-formaten kunnen worden geïntegreerd. 
                  Wij zorgen voor de juiste kijkhoogte en veilige montage.
                </p>
              </div>

              <div className="bg-stone-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Is een cinewall veilig met een haard?
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Ja, mits technisch correct opgebouwd. 
                  Wij houden rekening met ventilatie, 
                  materiaalkeuze en afstand tussen haard en tv.
                </p>
              </div>

              <div className="bg-stone-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Wat als ik mijn afmetingen niet precies weet?
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Geen probleem. 
                  U kunt een inschatting invullen in de configurator. 
                  Wij controleren dit altijd en meten indien nodig alles na.
                </p>
              </div>

              <div className="bg-stone-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Hoe nauwkeurig is de prijsindicatie?
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  De indicatie is gebaseerd op uw keuzes en geeft een realistisch beeld. 
                  De exacte prijs hangt af van specifieke materialen, afmetingen en montage. 
                  Na persoonlijk advies en inmeting ontvangt u een definitieve offerte.
                </p>
              </div>

              <div className="bg-stone-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Wat gebeurt er na het versturen?
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  U ontvangt direct een bevestiging per e-mail. Binnen 24 uur nemen wij 
                  telefonisch contact met u op om uw wensen te bespreken en een afspraak 
                  te plannen voor advies of inmeting.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
